import React from 'react';
import { motion } from 'framer-motion';
import { Utensils, ArrowRight } from 'lucide-react';
import DonateBtn from './DonateBtn'; 

const serviceGroups = [
  {
    id: "nutrition",
    title: "Child Nutrition",
    mainDesc: "Providing balanced, warm meals to combat malnutrition in Mityana.",
    details: [
      { img: "/images/1.jpg", story: "Daily balanced meals for growth." },
      { img: "/images/12.jpg", story: "Our founder personally prepares nutritious meals for over 100 children every week." },
      { img: "/images/101.jpg", story: "Kids enjoying their meals." }
    ]
  },
  {
    id: "medical",
    title: "Medical Outreach & Inclusion",
    mainDesc: "Emergency medical care and support for sick children and those with disabilities.",
    details: [
      { img: "/images/2.jpg", story: "Professional medical checkups." },
      { img: "/images/15.jpg", story: "Providing health items and mobility support to children with disabilities." },
      { img: "/images/13.jpg", story: "Walking with the sick in hospitals, ensuring no child is alone." }
    ]
  },
  {
    id: "spiritual",
    title: "Spiritual Growth",
    mainDesc: "Teaching Bible stories and distributing Bibles to nourish the soul.",
    details: [
      { img: "/images/3.jpg", story: "Nourishing the soul through faith." },
      { img: "/images/11.jpg", story: "Handing out Holy Bibles to children, providing spiritual guidance." },
      { img: "/images/14.jpg", story: "Teaching the next generation values of love and faith." }
    ]
  },
  {
    id: "education",
    title: "Education & Basic Needs",
    mainDesc: "School fees, supplies, and basic necessities for orphans to ensure a brighter future.",
    details: [
      { img: "/images/4.jpg", story: "Ensuring every child stays in school." },
      { img: "/images/16.jpg", story: "Distributing essential supplies like clothes and soap to families in extreme poverty." },
      { img: "/images/about3.jpg", story: "Together we can change lives." },
    ]
  }
];

export default function Services() {
  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        
        {/* Main Header */}
        <div className="text-center mb-20">
          <h2 className="text-gh-blue text-4xl md:text-6xl font-black italic uppercase">What We Do</h2>
          <div className="w-24 h-2 bg-gh-orange mx-auto mt-4 mb-8"></div>
          
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto"
          >
            <p className="text-lg md:text-xl text-slate-600 leading-relaxed font-medium italic">
              "Giving Hands Ministry Mityana is a Christ-centered sanctuary dedicated to 
              rescuing abandoned, orphaned, and disabled children. We provide <span className="text-gh-blue font-bold">transformation</span> by 
              combining physical nutrition with spiritual nourishment and quality education."
            </p>
          </motion.div>
        </div>

        <div className="space-y-24">
          {serviceGroups.map((group, idx) => (
            <React.Fragment key={group.id}>
              <div className="group">
                <div className="mb-10">
                  <h3 className="text-gh-blue text-3xl font-black uppercase mb-3 flex items-center gap-4">
                    <span className="text-gh-orange">0{idx + 1}</span> {group.title}
                  </h3>
                  <p className="text-slate-600 text-lg max-w-2xl border-l-4 border-gh-orange pl-4">
                    {group.mainDesc}
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {group.details.map((item, i) => (
                    <motion.div 
                      key={i}
                      initial={{ opacity: 0, scale: 0.95 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.1 }}
                      className="relative rounded-[2rem] overflow-hidden shadow-lg bg-slate-50 group/item"
                    >
                      <img 
                        src={item.img} 
                        alt={group.title} 
                        className="w-full h-auto block object-cover aspect-square md:aspect-[4/5] lg:aspect-square group-hover/item:scale-105 transition-transform duration-700" 
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-gh-blue/90 via-transparent to-transparent opacity-0 group-hover/item:opacity-100 transition-all duration-500 flex flex-col justify-end p-6">
                        <p className="text-white text-sm font-bold leading-tight uppercase tracking-wider">{item.story}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* NUTRITION DONATION CALLOUT */}
              {idx === 0 && (
                <motion.div 
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="bg-gh-blue rounded-[2.5rem] p-8 md:p-12 text-white relative overflow-hidden shadow-2xl flex flex-col md:flex-row items-center justify-between gap-8 border-b-4 border-gh-orange"
                >
                  <div className="relative z-10 flex items-center gap-6">
                    <div className="bg-white/10 p-5 rounded-full backdrop-blur-md">
                      <Utensils className="text-gh-orange w-10 h-10" />
                    </div>
                    <div>
                      <h4 className="text-2xl md:text-3xl font-black uppercase italic">Support Our Feeding Program</h4>
                      <p className="text-blue-100 font-medium max-w-md">
                        A small gift can provide nutritious meals for a child in Mityana and ensure no one goes to bed hungry.
                      </p>
                    </div>
                  </div>

                  {/* Properly passing the 'label' prop to read FEED A CHILD */}
<DonateBtn 
  label={
    <span className="flex items-center gap-3">
      FEED A CHILD <ArrowRight size={18} />
    </span>
  }
  className="relative z-10 group bg-gh-orange hover:bg-white text-white hover:text-gh-blue px-10 py-5 rounded-full font-black text-sm uppercase tracking-[0.2em] transition-all duration-500 flex items-center shadow-lg active:scale-95 border-none"
/>

                  <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/5 rounded-full blur-2xl"></div>
                  <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-gh-orange/10 rounded-full blur-2xl"></div>
                </motion.div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    </section>
  );
}